/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useHistory, useLocation } from 'react-router-dom';
import { Container, Form, Spinner, Button } from 'react-bootstrap';
import { debounce } from 'lodash';
import LoadingContent from 'components/LoadingContent';
import TopProcessBar from 'screen/selectCollege/TopProcessBar';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SelectInput from 'components/input/SelectInput';
import SiblingsConfig from './SiblingsConfig';
import { getSiblings, updateSiblings } from 'app/family/familySlice';
import { routers } from 'router/router';
import { logout } from 'app/basicInformationSlice/basicInformationSlice';
import { setArrayLength } from 'components/helper/helper';
import Grid from '@mui/joy/Grid';
import { ModalCheckFamilyField } from '../common/ModalCheckMissingField';
import SearchIcon from '@mui/icons-material/Search';
import pageStatusService from 'services/pageStatusService';
import { PAGE_NAME } from 'constants/common';
import websocketService from 'services/websocketService';
import { getAccessToken, getUserId, removeAccessToken, removeUserId } from 'utils/sessionStorage';

const Siblings = () => {
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);
  const [showModalCheckField, setShowModalCheckField] = useState(false);
  const [reviewedAndComplete, setReviewedAndComplete] = useState(false);

  const family = useAppSelector((store) => store.family);
  const [arraySiblings, setArraySiblings] = useState<any[]>([]);
  const dispatch = useAppDispatch();
  const history = useHistory();
  let studentId = getUserId();
  let token = getAccessToken(); 
  const [initialLoad, setInitialLoad] = useState(true);
  const query = new URLSearchParams(useLocation().search);
  const check = query.get('check'); const formik = useFormik({
    initialValues: {
      number_of_siblings: 0,
      siblings_array: '',
    },
    onSubmit: async (values) => {
      studentId = getUserId();
      token = getAccessToken();
      if (token && studentId) {
        let newValues: any = {
          ...values,
          student_id: parseInt(studentId || ''),
          siblings_array: JSON.stringify(arraySiblings),
        };

        await dispatch(updateSiblings(newValues));
      }
      else {
        handleLogout();
      }

    },
  });

  const handleLogout = () => {
    dispatch(logout());
    removeAccessToken();
    removeUserId();
    history.push(routers.login.path);
  };

  const handleContinue = () => {
    formik.handleSubmit();
    history.push(routers.enrolimentStatus.path);
  };

  const handleCheckMissingFields = () => {
    setShowModalCheckField(true);
  };

  const handleCloseModal = () => {
    setShowModalCheckField(false);
  };

  // Prepare data for modal check
  const getModalData = () => {
    return {
      ...formik.values,
      siblings_array: arraySiblings,
    };
  };

  const getDetail = async () => {
    studentId = getUserId();
    token = getAccessToken();
    if (token && studentId) {

      setLoading(true);
      const pageStatus: {
        student_completed: boolean;
      } = await pageStatusService.getPageStatusByPageName(studentId, PAGE_NAME.SIBLINGS);
      if(pageStatus) {
        setReviewedAndComplete(pageStatus.student_completed);
      }
      let res = await dispatch(getSiblings(studentId));
      const dataLoaded: any = res.payload;
      formik.resetForm({ values: { ...dataLoaded, number_of_siblings: dataLoaded.number_of_siblings || 0 } });
      if (dataLoaded.siblings_array) {
        const arraySibling = JSON.parse(dataLoaded.siblings_array);
        setArraySiblings(arraySibling);
      }
      setLoading(false);
      setInitialLoad(false);
    } else {
      handleLogout();
    }
  };

  useEffect(() => {
    getDetail();
  }, []);

  useEffect(() => {
    if (!initialLoad) {
      setArraySiblings(setArrayLength(arraySiblings, formik.values.number_of_siblings));
    }
  }, [formik.values.number_of_siblings]);

  const debouncedSubmit = React.useCallback(
    debounce(() => {
      if (!loading && !initialLoad) {
        formik.handleSubmit();
      }
    }, 1500),
    [formik.values, arraySiblings, loading, initialLoad]
  );

  useEffect(() => {
    if (!initialLoad) {
      debouncedSubmit();
    }
    // Cleanup debounce on unmount
    return () => {
      debouncedSubmit.cancel();
    };
  }, [formik.values, arraySiblings, debouncedSubmit]);

  React.useEffect(() => {
    // Handle responsive detection
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onReviewdAndComplete = () => { 
    const newValue = !reviewedAndComplete;
    setReviewedAndComplete(newValue);
    websocketService.finishReviewAndComplete(PAGE_NAME.SIBLINGS, newValue);
  }

  return loading ? (
    <LoadingContent />
  ) : (
    <>
      <TopProcessBar
        stepProcess={[
          { title: 'Number of Siblings', percentOnProcess: 25, specialId: 'Siblings' },
          { title: 'Siblings Details', percentOnProcess: 99, specialId: 'Siblings Details' },
        ]}
        title="Family Information"
        process={1}
        icon={<PeopleAltOutlinedIcon />}
        isMobile={isMobile}
      />
      <Container 
        className={`main-container gm-basic-info-page ${isMobile ? 'p-mobile-2' : ''}`}
        style={{
          paddingLeft: isMobile ? '12px' : undefined,
          paddingRight: isMobile ? '12px' : undefined,
          paddingTop: isMobile ? '60px' : undefined,
          maxWidth: '100vw',
          overflow: 'hidden'
        }}
      >
        <div className={`gm-section-card ${isMobile ? 'p-mobile-2' : 'px-4 py-4'}`}>
          <Form 
            style={{ 
              maxWidth: isMobile ? '100%' : '900px',
              width: '100%'
            }} 
            onSubmit={formik.handleSubmit} 
            className="gm-form-grid"
          >
            <div className={`gm-section-title ${isMobile ? 'mb-2' : 'mb-4'}`}>
              <span style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 600 }}>
                Siblings
              </span>
            </div>
            <Grid container spacing={isMobile ? 1 : 2} className="gm-form-fields">
              <Grid xs={12}>
                <SelectInput
                  disabled={reviewedAndComplete}
                  logo="span-cm-logo"
                  form={formik}
                  name="number_of_siblings"
                  values={formik.values.number_of_siblings || 0}
                  onChange={formik.handleChange}
                  label="Please specify number of siblings you have*"
                  isSatTest={true}
                  option={[
                    { value: 0 },
                    { value: 1 },
                    { value: 2 },
                    { value: 3 },
                    { value: 4 },
                    { value: 5 },
                  ]}
                  isMissing={
                    !formik.values.number_of_siblings && check
                  }
                />
              </Grid>
              <div className='bg-white p-3 rounded-3 w-100'>
              {arraySiblings.map((item, index) => (
                <Grid xs={12} key={index}>
                  <SiblingsConfig
                    check={check}
                    item={item}
                    total={arraySiblings.length}
                    current={index}
                    handleSave={(values) => {
                      const newArraySiblings = [...arraySiblings];
                      newArraySiblings.splice(index, 1, values);
                      setArraySiblings(newArraySiblings);
                    }}
                    expandedAll={check ? true : false}
                    isMobile={isMobile}
                    disabled={reviewedAndComplete}
                  />
                </Grid>
              ))}
              </div>
            </Grid>
            <div className={`gm-form-actions border-top ${isMobile ? 'pt-2 mt-3' : 'pt-3 mt-4'} d-flex ${isMobile ? 'flex-column' : 'justify-content-between align-items-center'}`}>
              {/* Icon + Checkbox combo on the left */}
              <div className={`${isMobile ? 'w-100 mb-3' : ''} d-flex justify-content-center`}>
                <div className="d-flex flex-column align-items-center">
                  <div
                    onClick={handleCheckMissingFields}
                    className="d-flex align-items-center cursor-pointer mb-2"
                  >
                    <SearchIcon
                      style={{
                        fontSize: '24px',
                        color: '#6c757d',
                      }}
                    />
                    <span
                      style={{
                        fontSize: '14px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      check missing values
                    </span>
                  </div>

                  <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                    <input
                      type="checkbox"
                      style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #6c757d',
                        borderRadius: '2px',
                      }}
                      checked={reviewedAndComplete}
                      onClick={onReviewdAndComplete}
                    />
                    <span
                      style={{
                        fontSize: isMobile ? '14px' : '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Reviewed & Complete!
                    </span>
                  </div>
                </div>
              </div>

              {/* Save and Continue buttons on the right */}
              <div className={`${isMobile ? 'w-100' : 'col col-md-6'} text-center ${isMobile ? '' : 'text-md-end'}`}>
                <div className={`d-flex ${isMobile ? 'flex-column gap-2' : 'flex-row'} justify-content-center`}>
                  <Button 
                    variant="primary" 
                    type="submit" 
                    className={`${isMobile ? 'w-100 py-3' : 'p-2 px-5 mx-2'} gm-btn-save`}
                    style={{
                      fontSize: isMobile ? '16px' : '14px',
                      minHeight: isMobile ? '44px' : 'auto'
                    }}
                  >
                    {family.status === 'loading' ? (
                      <Spinner size="sm" animation="border" variant="light" />
                    ) : (
                      'Save'
                    )}
                  </Button>
                  <Button 
                    variant="primary" 
                    className={`${isMobile ? 'w-100 py-3' : 'p-2 px-5'} gm-btn-continue`} 
                    onClick={handleContinue}
                    style={{
                      fontSize: isMobile ? '16px' : '14px',
                      minHeight: isMobile ? '44px' : 'auto'
                    }}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </Container>

      <ModalCheckFamilyField
        show={showModalCheckField}
        onClose={handleCloseModal}
        data={getModalData()}
        page="siblings"
      />
    </>
  );
};

export default Siblings;
